// Firebase configuration and services
import { initializeApp, getApps, getApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, User } from "firebase/auth";
import { getFirestore, collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, Timestamp, setDoc } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { getAnalytics } from "firebase/analytics";

// Types
export interface WebsiteSettings {
  clinic: {
    name: string;
    logo: string;
    headerImage: string;
    pageTitle: string;
    phone: string[];
    address: string[];
    workingHours: string[];
    clinicPhotos: string[];
    images: string[];
    socialMedia: {
      facebook: string;
      instagram: string;
      twitter: string;
      whatsapp: string;
    };
    contactInfo: {
      email: string;
      whatsapp: string;
      mapEmbed: string;
    };
    location: {
      mapEmbed: string;
    };
  };
  doctor: {
    name: string;
    title: string;
    about: string;
    image: string;
    qualifications: string[];
    statistics: {
      experienceYears: number;
      successfulCases: number;
      satisfactionRate: number;
    };
  };
  qualifications: Array<{
    title: string;
    institution: string;
    description: string;
    year: string;
  }>;
}

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyBcnwlgfj7QdOuWCKTSa4IY1O-l3uI-nu8",
  authDomain: "unison-app-3b062.firebaseapp.com",
  projectId: "unison-app-3b062",
  storageBucket: "unison-app-3b062.firebasestorage.app",
  messagingSenderId: "246897818823",
  appId: "1:246897818823:web:32f4ed46450556733c12e2",
  measurementId: "G-GF29VQP435"
};

// Initialize Firebase
const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const analytics = getAnalytics(app);

// Auth functions
export const loginWithEmail = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error) {
    console.error('Login error:', error);
    return { user: null, error };
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
    return { error: null };
  } catch (error) {
    console.error('Logout error:', error);
    return { error };
  }
};

export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Firestore functions
export const getWebsiteSettings = async (): Promise<{ data: WebsiteSettings | null; error: any }> => {
  try {
    const docRef = doc(db, 'settings', 'website');
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { data: docSnap.data() as WebsiteSettings, error: null };
    }
    return { data: null, error: 'No settings found' };
  } catch (error) {
    console.error('Error getting settings:', error);
    return { data: null, error };
  }
};

export const updateWebsiteSettings = async (settings: any) => {
  try {
    await setDoc(doc(db, 'settings', 'website'), settings);
    return { error: null };
  } catch (error) {
    console.error('Error updating settings:', error);
    return { error };
  }
};

export const resetWebsiteSettings = async () => {
  try {
    const defaultSettings = {
      doctor: {
        name: '',
        title: '',
        image: '',
        about: '',
        qualifications: ['', '', '', '', '', '', '', ''],
        statistics: {
          experienceYears: 0,
          successfulCases: 0,
          satisfactionRate: 0
        }
      },
      clinic: {
        name: 'دنتال جلو',
        logo: '/logo.svg',
        headerImage: '/header-placeholder.jpg',
        pageTitle: 'دنتال جلو | عيادة طب الأسنان',
        phone: ['+20 123 456 7890'],
        address: ['شارع النيل، المهندسين، الجيزة، مصر'],
        workingHours: ['السبت - الخميس: 9:00 ص - 9:00 م'],
        clinicPhotos: [],
        images: [],
        socialMedia: {
          facebook: 'https://facebook.com/dentalglow',
          instagram: 'https://instagram.com/dentalglow',
          twitter: 'https://twitter.com/dentalglow',
          whatsapp: '+201234567890'
        },
        contactInfo: {
          email: 'info@dentalglow.com',
          whatsapp: '+20 123 456 7890',
          mapEmbed: '<iframe src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1709.12970219841!2d30.84281233229951!3d31.046877317892314!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14f64d78fba93181%3A0xc8a9822d65f3544a!2z2YXYutiz2YTZhyDYr9in2YrZhQ!5e0!3m2!1sar!2seg!4v1749654081800!5m2!1sar!2seg" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>'
        },
        location: {
          mapEmbed: '<iframe src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d1709.12970219841!2d30.84281233229951!3d31.046877317892314!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14f64d78fba93181%3A0xc8a9822d65f3544a!2z2YXYutiz2YTZhyDYr9in2YrZhQ!5e0!3m2!1sar!2seg!4v1749654081800!5m2!1sar!2seg" width="600" height="450" style="border:0;" allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>'
        }
      },
      qualifications: []
    };
    await setDoc(doc(db, 'settings', 'website'), defaultSettings);
    return { error: null };
  } catch (error) {
    console.error('Error resetting settings:', error);
    return { error };
  }
};

// Booking functions
export const createBooking = async (bookingData: any) => {
  try {
    const bookingsRef = collection(db, 'bookings');
    const docRef = await addDoc(bookingsRef, {
      ...bookingData,
      createdAt: Timestamp.now(),
      status: 'pending'
    });
    return { id: docRef.id, error: null };
  } catch (error) {
    console.error('Error creating booking:', error);
    return { id: null, error };
  }
};

export const getBookings = async () => {
  try {
    const bookingsRef = collection(db, 'bookings');
    const q = query(bookingsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const bookings = querySnapshot.docs.map(doc => {
      const data = doc.data();
      let createdAt: Date;
      
      // التحقق من نوع createdAt
      if (data.createdAt && typeof data.createdAt.toDate === 'function') {
        // إذا كان Timestamp من Firebase
        createdAt = data.createdAt.toDate();
      } else if (data.createdAt instanceof Date) {
        // إذا كان Date عادي
        createdAt = data.createdAt;
      } else if (data.createdAt) {
        // إذا كان string أو timestamp
        createdAt = new Date(data.createdAt);
      } else {
        // إذا لم يكن موجود
        createdAt = new Date();
      }
      
      return {
        id: doc.id,
        ...data,
        createdAt
      };
    });
    return { data: bookings, error: null };
  } catch (error) {
    console.error('Error getting bookings:', error);
    return { data: [], error };
  }
};

export const updateBooking = async (id: string, bookingData: any) => {
  try {
    const bookingRef = doc(db, 'bookings', id);
    await updateDoc(bookingRef, bookingData);
    return { error: null };
  } catch (error) {
    console.error('Error updating booking:', error);
    return { error };
  }
};

export const deleteBooking = async (id: string) => {
  try {
    const bookingRef = doc(db, 'bookings', id);
    await deleteDoc(bookingRef);
    return { error: null };
  } catch (error) {
    console.error('Error deleting booking:', error);
    return { error };
  }
};

// Storage functions
export const uploadImage = async (file: File, path: string) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return { url: downloadURL, error: null };
  } catch (error) {
    console.error('Error uploading image:', error);
    return { url: null, error };
  }
};

export const deleteImage = async (path: string) => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    return { error: null };
  } catch (error) {
    console.error('Error deleting image:', error);
    return { error };
  }
};

export const getCollection = async (collectionName: string) => {
  try {
    const collectionRef = collection(db, collectionName);
    const querySnapshot = await getDocs(collectionRef);
    const data = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { data, error: null };
  } catch (error) {
    console.error(`Error getting ${collectionName}:`, error);
    return { data: null, error };
  }
};

// Case functions
export interface Case {
  id: string;
  title: string;
  description: string;
  category: string;
  beforeImage: string;
  afterImage: string;
  createdAt: Date;
}

export const getAllCases = async () => {
  try {
    const casesRef = collection(db, 'cases');
    const q = query(casesRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const cases = querySnapshot.docs.map(doc => {
      const data = doc.data();
      let createdAt: Date;
      
      // التحقق من نوع createdAt
      if (data.createdAt && typeof data.createdAt.toDate === 'function') {
        // إذا كان Timestamp من Firebase
        createdAt = data.createdAt.toDate();
      } else if (data.createdAt instanceof Date) {
        // إذا كان Date عادي
        createdAt = data.createdAt;
      } else if (data.createdAt) {
        // إذا كان string أو timestamp
        createdAt = new Date(data.createdAt);
      } else {
        // إذا لم يكن موجود
        createdAt = new Date();
      }
      
      return {
        id: doc.id,
        ...data,
        createdAt
      };
    });
    return cases;
  } catch (error) {
    console.error('Error getting cases:', error);
    return [];
  }
};

// Blog functions
export interface BlogPost {
  id: string;
  title: string;
  content: string;
  excerpt: string;
  image: string;
  author: string;
  createdAt: Date;
}

export const getAllBlogPosts = async () => {
  try {
    const postsRef = collection(db, 'blog');
    const q = query(postsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => {
      const data = doc.data();
      let createdAt: Date;
      
      // التحقق من نوع createdAt
      if (data.createdAt && typeof data.createdAt.toDate === 'function') {
        // إذا كان Timestamp من Firebase
        createdAt = data.createdAt.toDate();
      } else if (data.createdAt instanceof Date) {
        // إذا كان Date عادي
        createdAt = data.createdAt;
      } else if (data.createdAt) {
        // إذا كان string أو timestamp
        createdAt = new Date(data.createdAt);
      } else {
        // إذا لم يكن موجود
        createdAt = new Date();
      }
      
      return {
        id: doc.id,
        ...data,
        createdAt
      };
    });
    return posts;
  } catch (error) {
    console.error('Error getting blog posts:', error);
    return [];
  }
};

export { app, auth, db, storage, analytics };